import React, {useState, useEffect, useRef} from 'react';
import styles from './FiltersBar.module.css';
import {IconButton} from '@material-ui/core';
import {
  ChevronLeft,
  ChevronRight,
  Event as EventIcon,
  Tune as FilterIcon,
} from '@material-ui/icons';
import EventFilters from './EventFilters';
import {filters} from '../../models/Filters';
import {
  logToggleRunWalk,
  logToggleHikeRuck,
  logToggleSocial,
  logAccessFilters,
} from '../../../../shared/models/Analytics';
import {
  EVENT_TAB_TYPES,
  MY_EVENT_TYPES,
  GROUP_EVENT_TAB_TYPES,
  PAST_EVENT_TYPES,
} from '../../../../shared/constants/EventTabs';
import {
  DATE_OPTIONS,
  FEATURED_LOZENGE_COUNT,
  GROUP_EVENT_OPTIONS,
  VIRTUAL_EVENT_SUBTYPES,
  VIRTUAL_GROUP_OPTIONS,
  WORKSPACE_ONLY_OPTIONS,
} from '../../../../shared/constants/EventFilters';
import {
  DEFAULT_LOCAL_OPTIONS,
  DEFAULT_VIRTUAL_OPTIONS,
} from '../../../../shared/constants/DefaultFilters';
import {projectConfig} from '../../../../shared/project-info/projectConfig';
import {
  pickEventConfigValue,
  pickProjectConfigValue,
} from '../../../../shared/utils/ProjectConfigPicker';

const hasLocalGroupFilters =
  pickProjectConfigValue('hasGroups') &&
  !projectConfig.allowsLocalEventsOutsideGroups;

const Lozenge = ({slug, display, onClick, selected, group}) => (
  <IconButton
    className={styles.filterLozenges}
    size={'small'}
    onClick={onClick}>
    <div
      className={[
        styles.singleContainer,
        filters.eventCategory === slug && !group && styles.selected, // group statement is needed to avoid conflicts with selecting the first lozenge on group events
        filters.virtualEventGroupOption === slug && styles.selected,
        selected && styles.selected, // for my event types
      ].join(' ')}>
      {display}
    </div>
  </IconButton>
);

const GroupEventLozenges = ({activities, active, onChange}) => {
  return (
    <div className={styles.filtersContainer}>
      {activities.map(({display, slug}, i) => (
        <Lozenge
          key={i}
          slug={slug}
          display={display}
          onClick={() => onChange(slug)}
          selected={active === slug}
          group
        />
      ))}
    </div>
  );
};

const FiltersBar = ({
  onSubmit,
  activeTab,
  activeMyEventsType,
  onFilterMyEvents,
  virtualSubtype,
  virtualSortBy,
  virtualEventDate,
  virtualEventCategory,
  virtualWorkspaceOnly,
  groupId,
  activeGroupEventActivity,
  changeGroupEventActivity,
  activePastEventType,
  onChangeActivePastEventType,
}) => {
  const [openEventFilters, setopenEventFilters] = useState(false);
  const [filterCounter, setfilterCounter] = useState(
    filters.eventCategory === 'all-activities' ? 1 : 0,
  );
  const [filterOption, setFilterOption] = useState([]);
  const groupLozengesRef = useRef();

  useEffect(() => {
    const featuredLozenges = Object.values(GROUP_EVENT_OPTIONS).slice(
      0,
      FEATURED_LOZENGE_COUNT,
    );
    setFilterOption(
      pickEventConfigValue('eventFilterLozenges', featuredLozenges),
    );
  }, []);

  // when the active tab changes, update the count
  useEffect(() => {
    filterCounterHandler();
  }, [activeTab]);

  const eventFilterHandler = (isSubmit) => {
    setopenEventFilters((prevState) => !prevState);
    filterCounterHandler();

    if (isSubmit) {
      onSubmit();
    }
  };

  /* 
    Unideal code to keep the UX similar to mobile, despite how filters are handled being quite different.
    This fix is solely for when refreshing on the event page. Tab changes are properly handled in a use effect.
    This makes sure the filterCounterHandler is called and has the proper values from filters to deal with the async nature.
    Additionally, passing filters as props is not ideal as filter setting is done in this file.
  */
  setTimeout(() => {
    filterCounterHandler();
  }, 100);

  // handle the specific lozenge logging type
  // this is only called when one is selected, as opposed to deselecting the toggeled lozenge
  const filterLogType = (category) => {
    if (category === 'run-walk') logToggleRunWalk();
    else if (category === 'hike-ruck') logToggleHikeRuck();
    else if (category === 'social') logToggleSocial();
  };

  const filterEvents = async (category) => {
    if (category == 'workspace_only') {
      await filters.setFilter({
        workspaceOnly: filters.workspaceOnly == 'False' ? 'True' : 'False',
      });
      filters.getFilters();
    } else if (filters.eventCategory === category) {
      await filters.setFilter({
        eventCategory: 'all-activities',
      });
    } else {
      filterLogType(category);
      await filters.setFilter({
        eventCategory: category,
      });
    }
    filterCounterHandler();
    onSubmit();
  };

  const filterVirtualEvents = async (type) => {
    if (type == 'workspace_only') {
      await filters.setFilter({
        virtualWorkspaceOnly:
          virtualWorkspaceOnly == 'False' ? 'True' : 'False',
      });
      filters.getFilters();
    } else if (type == 'past-90') {
      await filters.setFilter({
        virtualEventDate: virtualEventDate == 'past-90' ? 'all' : 'past-90',
      });
      filters.getFilters();
    } else if (filters.virtualEventGroupOption === type) {
      await filters.setFilter({
        virtualEventGroupOption: VIRTUAL_GROUP_OPTIONS.national.slug,
      });
    } else {
      // TODO: Add analytics for Virtual Event Type if already not exists
      await filters.setFilter({
        virtualEventCategory:
          filters.virtualEventCategory === type ? 'all-activities' : type,
      });
    }
    filterCounterHandler();
    onSubmit();
  };

  const filterCounterHandler = () => {
    let counter = 0;
    if (activeTab === EVENT_TAB_TYPES.local) {
      if (filters.workspaceOnly !== DEFAULT_LOCAL_OPTIONS.workspaceOnly)
        counter++;
      if (filters.eventDistance !== DEFAULT_LOCAL_OPTIONS.eventDistance)
        counter++;
      if (filters.eventDate !== DEFAULT_LOCAL_OPTIONS.eventDate) counter++;
      if (filters.sortBy !== DEFAULT_LOCAL_OPTIONS.sortBy) counter++;
      if (filters.eventCategory !== DEFAULT_LOCAL_OPTIONS.eventCategory)
        counter++;
      if (filters.eventFilterNavTab !== DEFAULT_LOCAL_OPTIONS.eventFilterNavTab)
        counter++;
      if (
        (filters.eventFilterNavTab === 'group' || hasLocalGroupFilters) &&
        filters.eventGroupOption !== DEFAULT_LOCAL_OPTIONS.eventGroupOption
      )
        counter++;
    } else if (activeTab === EVENT_TAB_TYPES.virtual) {
      // only using props for virtual due to link specific virtual links
      if (virtualWorkspaceOnly !== DEFAULT_VIRTUAL_OPTIONS.virtualWorkspaceOnly)
        counter++;
      if (
        VIRTUAL_EVENT_SUBTYPES &&
        virtualSubtype !== DEFAULT_VIRTUAL_OPTIONS.virtualSubtype
      )
        counter++;
      if (virtualEventDate !== DEFAULT_VIRTUAL_OPTIONS.virtualEventDate)
        counter++;
      if (virtualSortBy !== DEFAULT_VIRTUAL_OPTIONS.virtualSortBy) counter++;
      if (virtualEventCategory !== DEFAULT_VIRTUAL_OPTIONS.virtualEventCategory)
        counter++;
      if (
        filters.virtualEventGroupOption !==
        DEFAULT_VIRTUAL_OPTIONS.virtualEventGroupOption
      )
        counter++;
      if (filters.virtualTime !== DEFAULT_VIRTUAL_OPTIONS.virtualTime)
        counter++;
    }
    setfilterCounter(counter);
  };

  return (
    <>
      <div className={styles.filtersContainer}>
        {['local', 'virtual'].includes(activeTab) && !groupId && (
          <IconButton
            className={styles.filterLozenges}
            size={'small'}
            onClick={() => {
              logAccessFilters();
              eventFilterHandler(false);
            }}>
            {filterCounter ? (
              <div className={`${styles.singleContainer} ${styles.selected}`}>
                <div className={styles.filterCounter}>{filterCounter}</div>
                Filters
              </div>
            ) : (
              <div className={styles.singleContainer}>
                <FilterIcon className={styles.lozengeIcon} />
                Filters
              </div>
            )}
          </IconButton>
        )}
        {projectConfig.hasWorkspaces &&
          Object.keys(WORKSPACE_ONLY_OPTIONS).length > 0 &&
          activeTab === EVENT_TAB_TYPES.local && (
            <Lozenge
              slug={'workspace_only'}
              display={`My Company Events`}
              onClick={() => filterEvents('workspace_only')}
              selected={filters.workspaceOnly === 'True'}
            />
          )}
        {activeTab === EVENT_TAB_TYPES.local && (
          <>
            {filterOption.map((item, index) => {
              return (
                <Lozenge
                  key={index}
                  slug={item.slug}
                  display={item.display}
                  onClick={() => filterEvents(item.slug)}
                  selected={item.slug == virtualEventCategory}
                />
              );
            })}
          </>
        )}
        {projectConfig.hasWorkspaces &&
          Object.keys(WORKSPACE_ONLY_OPTIONS).length > 0 &&
          activeTab === EVENT_TAB_TYPES.virtual && (
            <Lozenge
              slug={'workspace_only'}
              display={`My Company Events`}
              onClick={() => filterVirtualEvents('workspace_only')}
              selected={virtualWorkspaceOnly === 'True'}
            />
          )}
        {activeTab === EVENT_TAB_TYPES.virtual && (
          <>
            {projectConfig.hasPast90Lozenge && (
              <Lozenge
                slug={DATE_OPTIONS['past-90'].slug} //
                display={DATE_OPTIONS['past-90'].display}
                onClick={() =>
                  filterVirtualEvents(DATE_OPTIONS['past-90'].slug)
                }
                selected={virtualEventDate == DATE_OPTIONS['past-90'].slug}
              />
            )}
            {filterOption.map((item, index) => {
              return (
                <Lozenge
                  key={index}
                  slug={item.slug}
                  display={item.display}
                  onClick={() => filterVirtualEvents(item.slug)}
                  selected={item.slug == virtualEventCategory}
                />
              );
            })}
          </>
        )}
        {activeTab === EVENT_TAB_TYPES.my && (
          <>
            <Lozenge
              slug={MY_EVENT_TYPES.hosting}
              display={MY_EVENT_TYPES.hosting}
              onClick={() => onFilterMyEvents(MY_EVENT_TYPES.hosting)}
              selected={activeMyEventsType === MY_EVENT_TYPES.hosting}
            />
            <Lozenge
              slug={MY_EVENT_TYPES.upcoming}
              display={MY_EVENT_TYPES.upcoming}
              onClick={() => onFilterMyEvents(MY_EVENT_TYPES.upcoming)}
              selected={activeMyEventsType === MY_EVENT_TYPES.upcoming}
            />
            <Lozenge
              slug={MY_EVENT_TYPES.past}
              display={MY_EVENT_TYPES.past}
              onClick={() => onFilterMyEvents(MY_EVENT_TYPES.past)}
              selected={activeMyEventsType === MY_EVENT_TYPES.past}
            />
          </>
        )}
      </div>
      {openEventFilters && (
        <EventFilters
          onCloseFilters={eventFilterHandler}
          activeTab={activeTab}
        />
      )}
    </>
  );
};

export default FiltersBar;
